/**
 * Reconcile client-portal reschedule state and client notifications when a provider
 * moves or confirms an appointment from the calendar (outside the portal-reschedule bot).
 * Reads the pending request Task but never writes it (ENG-3764 D29).
 */
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Communication, Task } from '@medplum/fhirtypes';
import { DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE } from './providerAvailabilitySchedule';
import { formatInstantRangeInTimeZone } from '../../lib/dateHelpers';
import { notifyClientAppointmentTimeChanged } from '../../lib/notificationLifecycle';
import {
  APPOINTMENT_REQUEST_DETAILS_EXT_URL,
  type AppointmentRequestDetailsPayload,
} from '../../pages/messaging/appointmentRequestMessage';
import { findPendingRescheduleTaskForAppointment } from '../../pages/client-portal/utils/rescheduleRequestTask';
import { findPendingAppointmentRequestTaskForProposed } from '../../pages/client-portal/utils/appointmentRequestTask';
import { completePortalRescheduleDecision } from '../../pages/client-portal/services/completePortalRescheduleDecision';
import {
  getParticipantAttendanceStatus,
  getPatientParticipants,
} from '@unify/fhir-model/resources/appointment';

export const PORTAL_RESCHEDULE_ORIGINAL_APPOINTMENT_EXT =
  'https://unify.health/fhir/StructureDefinition/portal-reschedule-original-appointment';

export const CLINICIAN_CANCELLED_CALENDAR_STATUS = 'clinician-cancelled';

function patientIdFromAppointment(appt: Appointment): string | undefined {
  for (const p of appt.participant ?? []) {
    const ref = p.actor?.reference;
    if (ref?.startsWith('Patient/')) return ref.slice('Patient/'.length);
  }
  return undefined;
}

export function getPortalRescheduleOriginalId(appt: Appointment): string | undefined {
  const ext = appt.extension?.find((e) => e.url === PORTAL_RESCHEDULE_ORIGINAL_APPOINTMENT_EXT);
  const ref = ext?.valueReference?.reference;
  if (!ref?.startsWith('Appointment/')) return undefined;
  return ref.slice('Appointment/'.length);
}

export function appointmentTimesChanged(before: Appointment, after: Appointment): boolean {
  if (!before.start || !before.end || !after.start || !after.end) return false;
  const startDelta = Math.abs(new Date(before.start).getTime() - new Date(after.start).getTime());
  const endDelta = Math.abs(new Date(before.end).getTime() - new Date(after.end).getTime());
  return startDelta > 60_000 || endDelta > 60_000;
}

function isBookedStatus(status: Appointment['status'] | undefined): boolean {
  return status === 'booked' || status === 'pending';
}

function formatAppointmentRangeLabel(start: string, end: string, timeZone = DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE): string {
  return formatInstantRangeInTimeZone(start, end, timeZone);
}

function communicationIdFromTask(task: Task | undefined): string | undefined {
  const ref = task?.reasonReference?.reference;
  if (!ref?.startsWith('Communication/')) return undefined;
  return ref.slice('Communication/'.length);
}

async function patchPortalRescheduleRequestTimes(
  medplum: MedplumClient,
  originalAppointmentId: string,
  after: Appointment,
): Promise<void> {
  if (!after.start || !after.end) return;

  const task = await findPendingRescheduleTaskForAppointment(medplum, originalAppointmentId);
  if (!task?.id) return;

  // ENG-3764 D29: the request Task is not written (only task-events writes Tasks); the message carries the new time.
  const preferredWhen = formatAppointmentRangeLabel(after.start, after.end);

  const communicationId = communicationIdFromTask(task);
  if (!communicationId) return;

  try {
    const message = await medplum.readResource('Communication', communicationId);
    const extensions = [...(message.extension ?? [])];
    const detailsIndex = extensions.findIndex((e) => e.url === APPOINTMENT_REQUEST_DETAILS_EXT_URL);
    if (detailsIndex < 0) return;

    const raw = extensions[detailsIndex]?.valueString;
    if (!raw) return;

    const parsed = JSON.parse(raw) as AppointmentRequestDetailsPayload;
    parsed.requestedStart = after.start;
    parsed.requestedEnd = after.end;
    parsed.preferredWhen = preferredWhen;
    if (after.id) parsed.proposedAppointmentId = after.id;

    extensions[detailsIndex] = {
      ...extensions[detailsIndex]!,
      valueString: JSON.stringify(parsed),
    };

    await medplum.updateResource({
      ...message,
      extension: extensions,
    } satisfies Communication);
  } catch (err) {
    console.warn('[providerAppointmentScheduleSync] Could not patch reschedule message:', err);
  }
}

async function patchNewAppointmentRequestTimes(
  medplum: MedplumClient,
  after: Appointment,
): Promise<void> {
  if (!after.id || !after.start || !after.end) return;

  const task = await findPendingAppointmentRequestTaskForProposed(medplum, after.id);
  if (!task?.id) return;

  // ENG-3764 D29: the request Task is not written (only task-events writes Tasks); the message carries the new time.
  const preferredWhen = formatAppointmentRangeLabel(after.start, after.end);

  const communicationId = communicationIdFromTask(task);
  if (!communicationId) return;

  try {
    const message = await medplum.readResource('Communication', communicationId);
    const extensions = [...(message.extension ?? [])];
    const detailsIndex = extensions.findIndex((e) => e.url === APPOINTMENT_REQUEST_DETAILS_EXT_URL);
    if (detailsIndex < 0) return;

    const raw = extensions[detailsIndex]?.valueString;
    if (!raw) return;

    const parsed = JSON.parse(raw) as AppointmentRequestDetailsPayload;
    parsed.requestedStart = after.start;
    parsed.requestedEnd = after.end;
    parsed.preferredWhen = preferredWhen;
    parsed.proposedAppointmentId = after.id;

    extensions[detailsIndex] = {
      ...extensions[detailsIndex]!,
      valueString: JSON.stringify(parsed),
    };

    await medplum.updateResource({
      ...message,
      extension: extensions,
    } satisfies Communication);
  } catch (err) {
    console.warn('[providerAppointmentScheduleSync] Could not patch appointment request message:', err);
  }
}

function isCancelledFhirStatus(status: Appointment['status'] | undefined): boolean {
  return status === 'cancelled';
}

async function reconcilePendingPortalAppointmentRequest(
  medplum: MedplumClient,
  params: {
    before: Appointment;
    after: Appointment;
    calendarStatus?: string;
  },
): Promise<boolean> {
  const { before, after, calendarStatus } = params;
  if (!after.id) return false;

  const originalId = getPortalRescheduleOriginalId(after) ?? getPortalRescheduleOriginalId(before);
  const pendingTask = originalId
    ? await findPendingRescheduleTaskForAppointment(medplum, originalId)
    : await findPendingAppointmentRequestTaskForProposed(medplum, after.id);
  if (!pendingTask?.id) return false;

  const bookedNow = isBookedStatus(after.status);
  const confirmingHold =
    bookedNow
    && (calendarStatus === 'scheduled' || calendarStatus === 'confirmed' || before.status === 'proposed');

  if (confirmingHold) {
    await completePortalRescheduleDecision(medplum, {
      proposedAppointmentId: after.id,
      originalAppointmentId: originalId,
      decision: 'accept',
    });
    return true;
  }

  if (
    (calendarStatus === CLINICIAN_CANCELLED_CALENDAR_STATUS
      || getPatientParticipants(after).some(
        (p) => getParticipantAttendanceStatus(p) === 'clinician-cancelled',
      ))
    && (isCancelledFhirStatus(after.status) || after.status === 'booked')
  ) {
    await completePortalRescheduleDecision(medplum, {
      proposedAppointmentId: after.id,
      originalAppointmentId: originalId,
      decision: 'deny',
    });
    return true;
  }

  return false;
}

/**
 * After a provider calendar save/drag, sync portal reschedule tasks/messages and
 * notify the client when a booked session time changes.
 */
export async function syncProviderAppointmentScheduleChange(
  medplum: MedplumClient,
  params: { before: Appointment; after: Appointment; calendarStatus?: string },
): Promise<void> {
  const { before, after, calendarStatus } = params;
  const patientId = patientIdFromAppointment(after);
  if (!patientId || !after.id) return;

  const originalId = getPortalRescheduleOriginalId(after) ?? getPortalRescheduleOriginalId(before);
  const reconciled = await reconcilePendingPortalAppointmentRequest(medplum, {
    before,
    after,
    calendarStatus,
  });
  if (reconciled) return;

  const timeChanged = appointmentTimesChanged(before, after);
  const statusChanged = before.status !== after.status;
  if (!timeChanged && !statusChanged) return;

  if (originalId && before.status === 'proposed' && timeChanged) {
    await patchPortalRescheduleRequestTimes(medplum, originalId, after);
    return;
  }

  if (!originalId && before.status === 'proposed' && timeChanged) {
    await patchNewAppointmentRequestTimes(medplum, after);
    return;
  }

  if (isBookedStatus(after.status) && timeChanged && !originalId) {
    if (!after.start || !after.end) return;
    await notifyClientAppointmentTimeChanged(medplum, {
      patientId,
      appointmentId: after.id,
      whenLabel: formatAppointmentRangeLabel(after.start, after.end),
      previousWhenLabel:
        before.start && before.end
          ? formatAppointmentRangeLabel(before.start, before.end)
          : undefined,
    });
  }
}
